"use client";

import { useEffect, useState } from "react";
import { getCurrentDateUTC } from "@/lib/daily/dateUtils";
import { getQuizForDate } from "@/lib/daily/contentSelector";
import { QuizContent } from "@/lib/daily/types";
import QuizOfDay from "@/components/daily/quiz/QuizOfDay";
import DailyLoader from "@/components/daily/shared/DailyLoader";
import { purgePracticeStates } from "@/lib/daily/storageManager";
import type { YMALPoolItem } from "@/lib/blog/ymal-mapper";
import quizzesData from "@/data/daily/quizzes.json";

// Helper to get a random quiz that hasn't been played recently
function getRandomQuiz(recentIds: number[]): QuizContent {
  // Filter out recently played quizzes
  const availableQuizzes = quizzesData.filter(q => !recentIds.includes(q.id));
  
  // If all quizzes have been played recently, reset the history
  const pool = availableQuizzes.length > 0 ? availableQuizzes : quizzesData;
  
  const randomIndex = Math.floor(Math.random() * pool.length);
  return pool[randomIndex] as QuizContent;
}

export default function QuizPageClient({ ymalPool }: { ymalPool: YMALPoolItem[] }) {
  const [data, setData] = useState<{ date: string; content: QuizContent; mode: "daily" | "practice" } | null>(null);

  useEffect(() => {
    // Check if we're in practice mode
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const isPracticeMode = mode === "practice";

    if (isPracticeMode) {
      // Clean up state keys left behind by previous practice sessions
      purgePracticeStates();

      // Generate a random practice ID
      const practiceId = `practice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Get recent practice quiz IDs from localStorage (guarded — a corrupted
      // value must not crash the page into a permanent loader)
      const recentKey = "quiz_practice_recent";
      let recentIds: number[] = [];
      try {
        const recentStr = localStorage.getItem(recentKey);
        const parsed = recentStr ? JSON.parse(recentStr) : [];
        if (Array.isArray(parsed)) recentIds = parsed;
      } catch {
        recentIds = [];
      }
      
      // Select a random quiz avoiding recent ones
      const content = getRandomQuiz(recentIds);
      
      // Update recent history (keep last 20 quizzes)
      const updatedRecent = [content.id, ...recentIds].slice(0, 20);
      localStorage.setItem(recentKey, JSON.stringify(updatedRecent));
      
      setData({ date: practiceId, content, mode: "practice" });
    } else {
      // Daily mode
      const date = getCurrentDateUTC();
      const country = localStorage.getItem("selectedCountry") || "us";
      const content = getQuizForDate(date, country);
      setData({ date, content, mode: "daily" });
    }
  }, []);

  if (!data) {
    return <DailyLoader message="Loading today's quiz..." />;
  }

  return (
    <>
      <h1 className="sr-only">Finance Quiz of the Day</h1>
      <QuizOfDay content={data.content} date={data.date} mode={data.mode} ymalPool={ymalPool} />
    </>
  );
}
